import React,{useEffect,useCallback,useState} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faStar, faUsers, faClock, faPlay, 
  faCheckCircle, faRobot, faMicrochip, 
  faBrain, faCodeBranch, faLaptopCode, 
  faCertificate, faBriefcase
} from '@fortawesome/free-solid-svg-icons';
import './HeroSection.css';

const AIFundamentals = () => {
    const [showModal, setShowModal] = useState(false);
      const [formCourseName, setFormCourseName] = useState('AI Fundamentals');
      const [formData, setFormData] = useState({
        fullName: '',
        mobileNumber: '',
        email: '',
      });
    
      const openModal = () => setShowModal(true);
      const closeModal = useCallback(() => {
        setShowModal(false);
        setFormData({ fullName: '', mobileNumber: '', email: '' });
      }, []);
    
      useEffect(() => {
        const handleEsc = (e) => {
          if (e.key === 'Escape') closeModal();
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
      }, [closeModal]);
    
      const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'mobileNumber') {
          const digitsOnly = value.replace(/\D/g, '');
          if (digitsOnly.length <= 10) {
            setFormData(prev => ({ ...prev, [name]: digitsOnly }));
          }
        } else {
          setFormData(prev => ({ ...prev, [name]: value }));
        }
      };
    
      const handleSubmit = async (e) => {
        e.preventDefault();
        const { fullName, mobileNumber, email } = formData;
    
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
        if (!fullName || !emailRegex.test(email) || mobileNumber.length !== 10) {
          alert('Please fill in valid details.');
          return;
        }
    
        try {
          const response = await fetch('https://api.dikshasea.in/api/enrollments', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...formData, course: formCourseName }),
          });
    
          const result = await response.json();
          if (response.ok) {
            alert('Enrollment successful!');
            closeModal();
          } else {
            alert(result.message || 'Submission failed');
          }
        } catch (err) {
          console.error(err);
          alert('Error submitting form');
        }
      };
    
  return (
    <>
      <section className="hero-section">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <span className="badge bg-primary bg-opacity-10 text-white mb-3">New AI Fundamentals Course</span>
              <h1 className="hero-title">
                Learn <span className="hero-highlight">AI Fundamentals</span>
              </h1>
              <p className="hero-subtitle">
                Explore the foundational concepts of Artificial Intelligence including machine learning,
                neural networks, and AI ethics. Gain hands-on experience with real-world applications.
              </p>
              
              <div className="course-meta">
                <div className="meta-item">
                  <div className="meta-icon">
                    <FontAwesomeIcon icon={faStar} />
                  </div>
                  <div>
                    <div className="fw-bold">4.8/5.0</div>
                    <small className="text-muted">(2,300 reviews)</small>
                  </div>
                </div>
                <div className="meta-item">
                  <div className="meta-icon">
                    <FontAwesomeIcon icon={faUsers} />
                  </div>
                  <div>
                    <div className="fw-bold">10,000+</div>
                    <small className="text-muted">Students</small>
                  </div>
                </div>
                <div className="meta-item">
                  <div className="meta-icon">
                    <FontAwesomeIcon icon={faClock} />
                  </div>
                  <div>
                    <div className="fw-bold">45 hours</div>
                    <small className="text-muted">Content</small>
                  </div>
                </div>
              </div>
              
             <button className="btn btn-primary btn-lg px-4" onClick={openModal}>
                  Enroll Now
                </button>
            </div>
            <div className="col-lg-6 mt-5 mt-lg-0">
              <div className="ratio ratio-16x9">
                <img 
                  src="subcourses-images/ai-fundamentals.webp" 
                  alt="AI Fundamentals Course" 
                  className="img-fluid rounded-3 shadow"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="container">
        <div className="course-details">
          <div className="course-content">
            {/* About Course */}
            <section className="about-course">
              <h2 className="section-title">About This Course</h2>
              <p>Kickstart your journey into AI by understanding the key principles behind intelligent systems. This course introduces supervised and unsupervised learning, neural networks, and responsible AI development.</p>
              <p>Learn how AI is transforming industries and build models using popular tools like TensorFlow, Scikit-Learn, and more.</p>
            </section>

            {/* Curriculum */}
            <section className="curriculum">
              <h2 className="section-title">Curriculum</h2>
              
              <div className="curriculum-item">
                <h3><FontAwesomeIcon icon={faRobot} className="me-2" /> Module 1: Introduction to AI</h3>
                <p>Understand what Artificial Intelligence is and its impact</p>
                <div className="topics">
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> History and evolution of AI</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> AI vs Machine Learning vs Deep Learning</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Applications of AI</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Tools and frameworks</div>
                </div>
              </div>

              <div className="curriculum-item">
                <h3><FontAwesomeIcon icon={faBrain} className="me-2" /> Module 2: Machine Learning Essentials</h3>
                <p>Get hands-on with supervised and unsupervised learning algorithms</p>
                <div className="topics">
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Linear & logistic regression</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Decision trees and clustering</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Model evaluation techniques</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Bias and variance</div>
                </div>
              </div>

              <div className="curriculum-item">
                <h3><FontAwesomeIcon icon={faMicrochip} className="me-2" /> Module 3: Deep Learning Basics</h3>
                <p>Learn the foundation of neural networks and deep learning</p>
                <div className="topics">
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Neural network architecture</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Activation functions</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Forward & backward propagation</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> CNNs and RNNs</div>
                </div>
              </div>

              <div className="curriculum-item">
                <h3><FontAwesomeIcon icon={faCodeBranch} className="me-2" /> Module 4: Ethics and Responsible AI</h3>
                <p>Understand ethical issues and fairness in AI systems</p>
                <div className="topics">
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Bias in AI models</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Explainability and transparency</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Privacy and security</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Legal and social implications</div>
                </div>
              </div>

              <div className="curriculum-item">
                <h3><FontAwesomeIcon icon={faPlay} className="me-2" /> Module 5: Capstone Project</h3>
                <p>Apply your learning to solve a real-world AI challenge</p>
                <div className="topics">
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Dataset selection and preprocessing</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Model training and tuning</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Deployment strategy</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Presentation and documentation</div>
                </div>
              </div>
            </section>
          </div>

          <div className="course-sidebar">
            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon icon={faClock} />
              </div>
              <div className="info-content">
                <h4>Duration</h4>
                <p>10 Weeks (15 hrs/week)</p>
              </div>
            </div>
            
            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon icon={faCertificate} />
              </div>
              <div className="info-content">
                <h4>Level</h4>
                <p>Beginner to Intermediate</p>
              </div>
            </div>
            
            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon icon={faLaptopCode} />
              </div>
              <div className="info-content">
                <h4>Projects</h4>
                <p>5 Hands-on Projects</p>
              </div>
            </div>
            
            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon icon={faUsers} />
              </div>
              <div className="info-content">
                <h4>Prerequisites</h4>
                <p>Basic Python & Math</p>
              </div>
            </div>
            
            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon icon={faBriefcase} />
              </div>
              <div className="info-content">
                <h4>Certifications</h4>
                <p>AI-900, TensorFlow, Google AI</p>
              </div>
            </div>
            
            <button className="btn btn-primary btn-lg px-4" onClick={openModal}>
                  Enroll Now
                </button>
          </div>
        </div>
      </div>
      {/* MODAL */}
      {showModal && (
        <div
          className="modal d-block fade show"
          style={{ background: 'rgba(0,0,0,0.5)' }}
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content p-4 d-flex flex-column flex-md-row">
              <div className="w-100 w-md-50 mb-3 mb-md-0">
                <img
                  src="/img/enroll.jpg"
                  alt="Enrollment"
                  className="img-fluid h-100"
                  style={{ objectFit: 'cover', width: '100%' }}
                />
              </div>
              <div className="w-100 w-md-50 ps-md-4">
                <h6 className="text-primary">Course:</h6>
                <h4 className="fw-bold">AI Fundamentals</h4>
                <form onSubmit={handleSubmit} noValidate>
                  <input
                    className="form-control mb-3"
                    type="text"
                    name="fullName"
                    placeholder="Full Name"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                  />
                  <input
                    className="form-control mb-3"
                    type="text"
                    name="mobileNumber"
                    placeholder="Mobile Number"
                    value={formData.mobileNumber}
                    onChange={handleInputChange}
                    required
                    maxLength={10}
                  />
                  <input
                    className="form-control mb-3"
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                  <div className="d-flex justify-content-end">
                    <button type="button" className="btn btn-outline-dark me-2" onClick={closeModal}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">Submit</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIFundamentals;
